import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "@mdi/react";
import { mdiTrashCan } from "@mdi/js";
import { toast } from "react-toastify";
import { useAuth } from "../../contexts/AuthContext";
import AppHeader from "../../components/AppHeader";
import ConfirmationModal from "../../components/ConfirmationModal";
import CustomFormInput from "../../components/forms/CustomFormInput";
import { organizationApi } from "../../api/organization";
import "../Common.css";
import "../Dashboard.css";
import "../header.css";

/**
 * TicketingSettings Page - Ticketing Defaults Management
 *
 * Dedicated settings page for the Ticketing portal. Reached via the cog in the
 * top-right of the Ticketing portal header. Houses the "Ticketing Defaults"
 * accordion (default ticket types, refund policy, terms & conditions) that
 * previously lived in the org Settings page.
 *
 * Data home is unchanged: organisations.settings.ticketingDefaults, read/written
 * via organizationApi.getSettings / updateSettings.
 *
 * Page is built around a single, always-open accordion. To add a second
 * settings group later, add a key to settingsAccordion and a render function
 * following the same pattern.
 */
const DEFAULT_REFUND_POLICY =
    "Refunds are allowed up to 24 hours before the event. Processing fees are non-refundable. Contact organizer for special circumstances.";
const DEFAULT_TERMS =
    "By purchasing tickets to this event, you agree to abide by all venue rules and event policies. Tickets are non-transferable unless otherwise stated. The organizer reserves the right to refuse entry or remove attendees who violate event policies. Please arrive on time as late entry may not be permitted during performances.";

const TicketingSettings = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    // Single accordion — open by default (it is currently the only section).
    const [settingsAccordion, setSettingsAccordion] = useState({
        ticketingDefaults: true,
    });

    const toggleAccordionSection = sectionKey => {
        setSettingsAccordion(prev => ({ ...prev, [sectionKey]: !prev[sectionKey] }));
    };

    const [isLoadingSettings, setIsLoadingSettings] = useState(false);
    const [orgCurrency, setOrgCurrency] = useState("AUD");

    // Confirmation modal state (reset-to-defaults gate)
    const [showConfirmModal, setShowConfirmModal] = useState(false);
    const [confirmModalConfig, setConfirmModalConfig] = useState({
        title: "",
        message: "",
        confirmText: "Confirm",
        variant: "primary",
        onConfirm: null,
    });

    const [ticketingDefaults, setTicketingDefaults] = useState({
        defaultTicketTypes: [{ id: 1, name: "General Admission", price: 25, currency: "AUD", description: "" }],
        defaultRefundPolicy: DEFAULT_REFUND_POLICY,
        defaultTermsAndConditions: DEFAULT_TERMS,
        primaryDefaultIndex: 0,
    });

    // Ticketing defaults management
    const handleTicketingDefaultsChange = (field, value) => {
        setTicketingDefaults(prev => ({ ...prev, [field]: value }));
    };

    const addDefaultTicketType = () => {
        const newTicketType = {
            id: Date.now(),
            name: "",
            price: "",
            // Org base currency — the backend stamps it on actual event creation anyway.
            currency: orgCurrency || "AUD",
            description: "",
        };
        setTicketingDefaults(prev => ({
            ...prev,
            defaultTicketTypes: [...prev.defaultTicketTypes, newTicketType],
        }));
    };

    const removeDefaultTicketType = index => {
        setTicketingDefaults(prev => {
            const newTicketTypes = prev.defaultTicketTypes.filter((_, i) => i !== index);
            let newPrimaryIndex = prev.primaryDefaultIndex;

            if (index === prev.primaryDefaultIndex) {
                newPrimaryIndex = 0;
            } else if (index < prev.primaryDefaultIndex) {
                newPrimaryIndex = prev.primaryDefaultIndex - 1;
            }

            return {
                ...prev,
                defaultTicketTypes: newTicketTypes,
                primaryDefaultIndex: newPrimaryIndex,
            };
        });
    };

    const updateDefaultTicketType = (index, field, value) => {
        setTicketingDefaults(prev => ({
            ...prev,
            defaultTicketTypes: prev.defaultTicketTypes.map((ticket, i) =>
                i === index ? { ...ticket, [field]: value } : ticket
            ),
        }));
    };

    // Validation functions
    const validateDefaultTicketType = ticket => {
        return {
            name: !!ticket.name?.trim(),
            price:
                ticket.price !== undefined && ticket.price !== null && ticket.price !== "" && Number(ticket.price) >= 0,
            description: !!ticket.description?.trim(),
        };
    };

    const validateAllDefaultTicketTypes = () => {
        if (ticketingDefaults.defaultTicketTypes.length === 0) return false;
        return ticketingDefaults.defaultTicketTypes.every(ticket => {
            const validation = validateDefaultTicketType(ticket);
            return validation.name && validation.price && validation.description;
        });
    };

    const getDefaultTicketFieldError = (ticketIndex, field) => {
        const ticket = ticketingDefaults.defaultTicketTypes[ticketIndex];
        if (!ticket) return false;
        const validation = validateDefaultTicketType(ticket);
        return !validation[field];
    };

    // Save ticketing defaults
    const saveTicketingDefaults = async () => {
        try {
            if (!validateAllDefaultTicketTypes()) {
                toast.error(
                    "Please complete all required fields for ticket types. Name, price, and description are required."
                );
                return;
            }

            setIsLoadingSettings(true);

            await organizationApi.updateSettings(user.organisationId, { ticketingDefaults });

            toast.success("Ticketing defaults saved successfully!");
            await loadTicketingSettings();
        } catch (error) {
            console.error("Error saving ticketing defaults:", error);
            toast.error(error.data?.message || error.message || "Failed to save ticketing defaults. Please try again.");
        } finally {
            setIsLoadingSettings(false);
        }
    };

    // Load ticketing defaults + org base currency
    const loadTicketingSettings = async () => {
        try {
            setIsLoadingSettings(true);

            const settings = await organizationApi.getSettings(user.organisationId);

            if (settings.ticketingDefaults) {
                setTicketingDefaults(prev => ({
                    ...prev,
                    ...settings.ticketingDefaults,
                    defaultRefundPolicy: settings.ticketingDefaults.defaultRefundPolicy || prev.defaultRefundPolicy,
                    defaultTermsAndConditions:
                        settings.ticketingDefaults.defaultTermsAndConditions || prev.defaultTermsAndConditions,
                }));
            }

            const org = await organizationApi.getOrgProfile(user.organisationId);
            if (org?.currency) setOrgCurrency(org.currency);
        } catch (error) {
            console.error("Error loading ticketing settings:", error);
        } finally {
            setIsLoadingSettings(false);
        }
    };

    useEffect(() => {
        if (user?.organisationId) {
            loadTicketingSettings();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [user?.organisationId]);

    const renderTicketingDefaults = () => (
        <div className="settings-accordion-section">
            <div
                className={`settings-accordion-header ${settingsAccordion.ticketingDefaults ? "expanded" : ""}`}
                onClick={() => toggleAccordionSection("ticketingDefaults")}
            >
                <h4>Ticketing Defaults</h4>
                <span className="accordion-icon">{settingsAccordion.ticketingDefaults ? "−" : "+"}</span>
            </div>

            {settingsAccordion.ticketingDefaults && (
                <div className="settings-accordion-content">
                    <p className="accordion-description">
                        Set up your default ticket types, pricing, and policies. These will be pre-filled when creating
                        new events.
                    </p>

                    {/* Default Ticket Types */}
                    <div className="settings-subsection">
                        <h5>Default Ticket Types</h5>
                        <p className="subsection-description">
                            Set up your default ticket types. The primary default will be used as the main ticket type
                            for new events.
                        </p>
                        {ticketingDefaults.defaultTicketTypes.map((ticket, index) => (
                            <div key={ticket.id || index} className="default-ticket-row">
                                <div className="ticket-header-row">
                                    <div className="primary-default-selector">
                                        <CustomFormInput
                                            type="radio"
                                            variant="purple"
                                            layout="standard"
                                            id={`primary-${ticket.id || index}`}
                                            name="primary-default"
                                            value={index}
                                            label={
                                                ticketingDefaults.primaryDefaultIndex === index
                                                    ? "Primary Default"
                                                    : "Set as Primary"
                                            }
                                            checked={ticketingDefaults.primaryDefaultIndex === index}
                                            onChange={() => handleTicketingDefaultsChange("primaryDefaultIndex", index)}
                                        />
                                    </div>
                                    {ticketingDefaults.defaultTicketTypes.length > 1 && (
                                        <button
                                            type="button"
                                            onClick={() => removeDefaultTicketType(index)}
                                            className="btn-remove"
                                            title="Remove ticket type"
                                        >
                                            <Icon path={mdiTrashCan} size={0.8} />
                                        </button>
                                    )}
                                </div>
                                <div className="form-row" style={{ justifyContent: "flex-start" }}>
                                    <div className="form-group quarter-width">
                                        <label>Ticket Name *</label>
                                        <input
                                            type="text"
                                            value={ticket.name}
                                            onChange={e => updateDefaultTicketType(index, "name", e.target.value)}
                                            placeholder="e.g. General Admission"
                                            className={`form-input ${getDefaultTicketFieldError(index, "name") ? "error" : ""}`}
                                        />
                                    </div>
                                    <div className="form-group" style={{ flex: "0 0 auto", width: "96px" }}>
                                        <label style={{ whiteSpace: "nowrap" }}>Price ({orgCurrency || "AUD"}) *</label>
                                        <input
                                            type="text"
                                            inputMode="decimal"
                                            value={ticket.price === "" ? "" : ticket.price}
                                            onFocus={e => e.target.select()}
                                            onChange={e => {
                                                // type="text" so non-numeric input (e.g. "gfd") actually
                                                // reaches here instead of being swallowed by the number
                                                // input. Strip anything that isn't a digit or dot, then
                                                // store "" for empty/non-numeric so validation rejects it
                                                // ($0 stays a valid free ticket).
                                                const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                                                const parsed = parseFloat(cleaned);
                                                updateDefaultTicketType(
                                                    index,
                                                    "price",
                                                    cleaned === "" || isNaN(parsed) ? "" : parsed
                                                );
                                            }}
                                            placeholder="Enter price"
                                            className={`form-input ${getDefaultTicketFieldError(index, "price") ? "error" : ""}`}
                                            style={{ width: "100%" }}
                                        />
                                    </div>
                                    <div className="form-group" style={{ flex: "1" }}>
                                        <label>Ticket Type Description *</label>
                                        <p
                                            className="subsection-description"
                                            style={{ fontStyle: "italic", marginBottom: "8px" }}
                                        >
                                            Displayed to ticket purchasers - explain what this ticket type includes
                                        </p>
                                        <textarea
                                            value={ticket.description}
                                            onChange={e =>
                                                updateDefaultTicketType(index, "description", e.target.value)
                                            }
                                            placeholder="e.g. Standard entry, includes welcome drink and beginner class"
                                            className={`form-textarea ${getDefaultTicketFieldError(index, "description") ? "error" : ""}`}
                                            rows={2}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}
                        <button onClick={addDefaultTicketType} className="secondary-action-btn">
                            + Add Ticket Type
                        </button>
                    </div>

                    {/* Refund Policy */}
                    <div className="settings-subsection">
                        <h5>Default Refund Policy</h5>
                        <p className="subsection-description">
                            This policy will be shown to customers during checkout and printed on tickets.
                        </p>
                        <textarea
                            value={ticketingDefaults.defaultRefundPolicy}
                            onChange={e => handleTicketingDefaultsChange("defaultRefundPolicy", e.target.value)}
                            placeholder="Enter your default refund policy..."
                            className="form-textarea"
                            rows={3}
                        />
                    </div>

                    {/* Terms and Conditions */}
                    <div className="settings-subsection">
                        <h5>Default Terms & Conditions</h5>
                        <p className="subsection-description">
                            These terms will be shown to customers during checkout and must be accepted before purchase.
                        </p>
                        <textarea
                            value={ticketingDefaults.defaultTermsAndConditions}
                            onChange={e => handleTicketingDefaultsChange("defaultTermsAndConditions", e.target.value)}
                            placeholder="Enter your default terms and conditions..."
                            className="form-textarea"
                            rows={4}
                        />
                    </div>

                    {/* Action Buttons */}
                    <div className="settings-actions">
                        <button
                            onClick={() => {
                                setConfirmModalConfig({
                                    title: "Reset to Defaults",
                                    message: `This will replace all your current ticket types (${ticketingDefaults.defaultTicketTypes.length} ticket type${ticketingDefaults.defaultTicketTypes.length !== 1 ? "s" : ""}) with a single General Admission ticket and restore the default refund policy and terms. This cannot be undone.`,
                                    confirmText: "Reset",
                                    variant: "danger",
                                    onConfirm: () => {
                                        setTicketingDefaults({
                                            defaultTicketTypes: [
                                                {
                                                    id: 1,
                                                    name: "General Admission",
                                                    price: 25,
                                                    currency: orgCurrency || "AUD",
                                                    description: "",
                                                },
                                            ],
                                            defaultRefundPolicy: DEFAULT_REFUND_POLICY,
                                            defaultTermsAndConditions: DEFAULT_TERMS,
                                            primaryDefaultIndex: 0,
                                        });
                                        setShowConfirmModal(false);
                                    },
                                });
                                setShowConfirmModal(true);
                            }}
                            className="secondary-action-btn"
                        >
                            Reset to Defaults
                        </button>
                        <button
                            onClick={saveTicketingDefaults}
                            disabled={isLoadingSettings}
                            className="primary-action-btn"
                        >
                            {isLoadingSettings ? "Saving..." : "Save Ticketing Defaults"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );

    return (
        <div className="dashboard-container org-console">
            <AppHeader />

            <div className="dashboard-main">
                <div className="dashboard-content-wrapper">
                    {/* Settings container — mirrors the org Settings page (white card wrapping header + accordions) */}
                    <div className="tab-content-settings">
                        {/* Page Header with Back Button + settings cog */}
                        <div className="portal-header-title-row">
                            <button onClick={() => navigate("/ticketing")} className="back-nav-btn">
                                ← Ticketing
                            </button>
                            <h1 className="portal-header-title">Ticketing Settings</h1>
                        </div>

                        {/* Accordion sections */}
                        {renderTicketingDefaults()}
                    </div>
                </div>
            </div>

            <ConfirmationModal
                isOpen={showConfirmModal}
                title={confirmModalConfig.title}
                message={confirmModalConfig.message}
                confirmText={confirmModalConfig.confirmText}
                variant={confirmModalConfig.variant}
                onConfirm={confirmModalConfig.onConfirm}
                onCancel={() => setShowConfirmModal(false)}
            />
        </div>
    );
};

export default TicketingSettings;
